import { useMemo } from 'react';
import * as R from 'remeda';
import { useSelector } from 'react-redux';
import { DrawerConfig } from '../../../../../containers/DocumentValidation/ValidationEmailDrawer';
import { reautomateEnabledSelector } from '../../../../../redux/modules/organizationGroup/selectors';
import { sidebarActionsOrderSelector } from '../../../../../redux/modules/user/selectors';
import { Annotation } from '../../../../../types/annotation';
import { useAnnotationNavigationActions } from './useAnnotationNavigationActions';
import { useBypassWorkflowAction } from './useBypassWorkflowAction';
import { useDeleteAction } from './useDeleteAction';
import { useDownloadAction } from './useDownloadAction';
import { useEmailAction } from './useEmailAction';
import { useLinkAction } from './useLinkAction';
import { useMoveAction } from './useMoveAction';
import { usePostponeAndRejectAction } from './usePostponeAndRejectAction';
import { useReAutomateAction } from './useReAutomateAction';
import { useReExtractAction } from './useReExtractAction';
import { SidebarFooterAction } from './utils';

type UseSidebarFooterActionsResult = {
  actions: SidebarFooterAction[];
  postponeAndReject: {
    modal: JSX.Element | null;
  };
  move: {
    modal: JSX.Element | null;
  };
  link: {
    modal: JSX.Element | null;
  };
  reExtract: {
    modal: JSX.Element | null;
  };
  reAutomate: {
    modal: JSX.Element | null;
  };
};

type UseSidebarFooterActions = (
  annotation: Annotation,
  onEmailThreadOpen: (drawerConfig?: DrawerConfig) => void
) => UseSidebarFooterActionsResult;

export const useSidebarFooterActions: UseSidebarFooterActions = (
  annotation,
  onEmailThreadOpen
) => {
  const {
    modal: postponeAndRejectModals,
    postpone: postponeAction,
    reject: rejectAction,
  } = usePostponeAndRejectAction(onEmailThreadOpen);

  const { previous: previousAction, next: nextAction } =
    useAnnotationNavigationActions(annotation);

  const { modal: moveModal, action: moveAction } = useMoveAction(annotation);

  const { action: emailAction } = useEmailAction(annotation, onEmailThreadOpen);

  const { modal: linkModal, action: linkAction } = useLinkAction(annotation);

  const { action: downloadAction } = useDownloadAction(annotation);

  const { modal: reExtractModal, action: reExtractAction } =
    useReExtractAction(annotation);

  const { modal: reAutomateModal, action: reAutomateAction } =
    useReAutomateAction(annotation);

  const isReAutomateEnabled = useSelector(reautomateEnabledSelector);

  const { action: deleteAction } = useDeleteAction(annotation);

  const { action: bypassAction } = useBypassWorkflowAction(annotation);

  const actionsOrder = useSelector(sidebarActionsOrderSelector);

  const orderMap = (actionsOrder ?? []).reduce<Record<string, number>>(
    (acc, action, index) => ({ ...acc, [action]: index }),
    {}
  );

  const allActions: SidebarFooterAction[] = useMemo(
    () =>
      R.pipe(
        [
          postponeAction,
          rejectAction,
          bypassAction,
          nextAction,
          previousAction,
          moveAction,
          emailAction,
          linkAction,
          downloadAction,
          reExtractAction,
          isReAutomateEnabled ? reAutomateAction : null,
          deleteAction,
        ],
        R.filter(R.isTruthy),
        R.sortBy(action => orderMap[action.id] ?? 0)
      ),
    [
      bypassAction,
      deleteAction,
      downloadAction,
      emailAction,
      isReAutomateEnabled,
      linkAction,
      moveAction,
      nextAction,
      orderMap,
      postponeAction,
      previousAction,
      reAutomateAction,
      reExtractAction,
      rejectAction,
    ]
  );

  return {
    actions: allActions,
    postponeAndReject: {
      modal: postponeAndRejectModals,
    },
    move: {
      modal: moveModal,
    },
    link: {
      modal: linkModal,
    },
    reExtract: {
      modal: reExtractModal,
    },
    reAutomate: {
      modal: reAutomateModal,
    },
  };
};
